import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "./AuthProvider";
import "./CardView.css";
import SaveButton from "./SaveButton";
import { useSavedRecs } from "../hooks/useSavedRecs";
import { useToggleSave } from "../hooks/useToggleSave";
import { usePreloadImages } from "../hooks/usePreloadImages";


export default function CardView({
  filteredRecords,
  hasRecommender,
  formatDate,
  getCategoryColor,
}) {
  const { session } = useAuth();
  const { data: savedIds = [] } = useSavedRecs(session?.user.id);
  const toggleSaveMutation = useToggleSave(session?.user.id);

  const [localSaved, setLocalSaved] = useState(new Set());
  const prevSavedIdsRef = useRef([]);

  usePreloadImages(filteredRecords);

  const handleToggle = async (recId) => {
    if (!session) {
      localStorage.setItem("pendingSave", recId);
      window.dispatchEvent(new CustomEvent("showVisitModal", {
        detail: {
          title: "Want to save this rec?",
          message: "Log in to stash this rec ❤️",
          buttons: [
            {
              label: "Log In",
              className: "login-button",
              onClick: () => {
                localStorage.setItem("fromModalRedirect", "true");
                const currentPath = window.location.pathname;
                const redirectPath = currentPath === "/" ? "/browse" : currentPath;
                window.location.href = `/login?redirect=${encodeURIComponent(redirectPath)}`;
              }
            },
            {
              label: "CREATE ACCOUNT",
              className: "link-button",
              onClick: () => {
                localStorage.setItem("fromModalRedirect", "true");
                const currentPath = window.location.pathname;
                const redirectPath = currentPath === "/" ? "/browse" : currentPath;
                window.location.href = `/login?view=sign_up&redirect=${encodeURIComponent(redirectPath)}`;
              }
            }
          ]
        }
      }));
      
      return;
    }
  
    const isSaved = localSaved.has(recId);
    const newSet = new Set(localSaved);
  
    if (isSaved) {
      newSet.delete(recId);
    } else {
      newSet.add(recId);
    }
  
    setLocalSaved(newSet);
  
    const { error } = await toggleSaveMutation.mutateAsync({ recId, isSaved });
  
    if (error) {
      // rollback
      if (isSaved) {
        newSet.add(recId);
      } else {
        newSet.delete(recId);
      }
      setLocalSaved(new Set(newSet));
    }
  };


  useEffect(() => {
    const prev = prevSavedIdsRef.current;
    const savedIdsChanged = JSON.stringify(prev) !== JSON.stringify(savedIds);
  
    if (savedIdsChanged) {
      console.log("🔄 Updating localSaved");
      setLocalSaved(new Set(savedIds));
      prevSavedIdsRef.current = savedIds;
    }
  }, [savedIds]);


  return (
    <div className="card-grid">
      {filteredRecords.map((record) => {
        const recId = record.fields.id || record.id;
        const isSaved = localSaved.has(recId);

        return (
          <div key={record.id} className="card">
            <div className="card-image-container">
              <img
                src={record.fields.ImageUrl || "/fallback.jpg"}
                alt={record.fields.Recommendation}
                className="card-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/fallback.jpg";
                }}
              />
            <SaveButton
              isSaved={isSaved}
              onToggle={() => handleToggle(recId)}
            />
            </div>
            <div className="card-content">
              <div className="card-title-row">
                <h3 className="card-title">{record.fields.Recommendation || "Untitled"}</h3>
              </div>
              <div className="card-meta-row">
                <div className="card-pill-group pills-inline">
                  {(record.fields?.Category || []).map((cat, idx) => (
                    <span
                      key={`cat-${idx}`}
                      className="category-pill"
                      style={{
                        backgroundColor: getCategoryColor(cat),
                        cursor: "default",
                      }}
                    >
                      {cat}
                    </span>
                  ))}
                  {hasRecommender &&
                    (record.fields?.Recommender || []).map((rec, idx) => (
                      <span
                        key={`rec-${idx}`}
                        className="category-pill recommender-pill"
                        style={{
                          backgroundColor: getCategoryColor(rec),
                          cursor: "default",
                        }}
                      >
                        {rec}
                      </span>
                    ))}
                </div>
                {record.fields?.Link && (
                  <a
                    href={record.fields.Link}
                    className="card-pill-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Link
                  </a>
                )}
              </div>
              <div className="card-details">
                <p className="episode-title">
                  <strong>Episode:</strong> {record.fields?.Title || "Untitled"}
                </p>
                <p className="episode-date">
                  <strong>Date:</strong> {formatDate(record.fields?.Date)}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
